import React from 'react';
import P from 'prop-types';
import { graphql, compose } from 'react-apollo';
import { Link } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import styled from 'styled-components';
import sanitizeHtml from 'sanitize-html';
import { allowedHtmlTags } from '@becollective/constants';

import { makeStacker } from 'components/ReactStacker/';
import Button from 'components/Button/';
import GraphQLErrorMessage from 'components/form/GraphQLErrorMessage/';
import GROUP_TYPES, { INDUSTRY_WITH_TYPES } from 'data/groupTypes';
import ErrorMsg from 'components/form/ErrorMsg/';
import PasswordMaskField from 'components/form/PasswordMaskField/';
import DateInputFields from 'components/form/DateInputFields/';
import { ThemeContext } from 'components/App/';
import schema from 'formSchemas/registerUser';
import { REGISTER_USER_MUTATION } from 'gql/mutations';
import { REGISTER_USER_QUERY } from 'gql/queries';
import displayLoading from 'utils/displayLoading';
import ensureLoggedIn from 'utils/ensureLoggedIn';
import transformErrors from 'utils/transformErrors';
import logo from 'assets/images/becollective-logo.svg';
import SelectWrap from 'components/SelectWrap/';
import VerifyEmail from '../VerifyEmail';

import './Register.scss';

const StyledDateInputFieldsWrapper = styled.div`
  width: 300px;
`;

const Register = ({ data, registerMutation, pushView }) => {
  const defaultRegisterPromoText = ({ name }) => {
    return `${name} provides vital links for promoting, facilitating, supporting and connecting volunteers and Volunteering Involving Organisations throughout our region.<br />
    <br />
    Volunteers and the tireless work of our community organisations are the glue that binds our community together. ${name} has invested in a digital volunteer management system that will help connect the people in our community with volunteer opportunities and enables our VIOs to schedule, communicate with and manage their volunteers.<br />
    <br />
    Additionally, the new system will provide much greater visibility of the outcomes that our VIOs create across the region and paint a picture of the positive impact that volunteers make in our community.<br />
    <br />
    The process below will walk you through creating your profile, registering your organisation and creating a volunteer opportunity so that your volunteer roles will appear on our website.<br />
    <br />`;
  };
  const { groupIndustries } = data.store;
  return (
    <ThemeContext.Consumer>
      {context => {
        const {
          loginText,
          loginButtonText,
          registerPromoText,
          hidePoweredByText,
          registerTitle,
          primary,
          registerSubmitButtonText,
        } = context;
        const registerText = registerPromoText ? registerPromoText : defaultRegisterPromoText(context);
        const { allowedTags, allowedAttributes } = allowedHtmlTags;
        const registerTextSanitised = sanitizeHtml(registerText || '', {
          allowedTags,
          allowedAttributes,
        });
        return (
          <div className="Register">
            <div className="Register-info">
              <div className="Register-text" dangerouslySetInnerHTML={{ __html: registerTextSanitised }} />
              <div className="Register-cta">
                <div className="Register-cta-label" style={{ color: primary ? primary : 'inherit' }}>
                  {loginText ? loginText : 'Have you already created a profile on Be Collective?'}
                </div>
                <Link className="ml-2" to="/login">
                  <Button color="primary">{loginButtonText ? loginButtonText : 'Log in'}</Button>
                </Link>
              </div>
            </div>
            <div className="Register-main">
              <h2 className="Register-heading">{registerTitle || 'Register now'}</h2>
              {!hidePoweredByText && (
                <div className="Register-bc">
                  Powered by <img className="Register-bcLogo" src={logo} alt="Be Collective" />
                </div>
              )}
              <Formik
                validationSchema={schema}
                initialValues={{
                  firstName: '',
                  lastName: '',
                  email: '',
                  password: '',
                  groupName: '',
                  groupType: '',
                  groupDesc: '',
                  groupIndustry: '',
                  birthdate: '',
                  mailingList: false,
                }}
                onSubmit={async (values, { setErrors, setSubmitting }) => {
                  const {
                    firstName,
                    lastName,
                    email,
                    password,
                    groupName,
                    groupType,
                    groupDesc,
                    groupIndustry,
                    birthdate,
                    mailingList,
                  } = values;

                  localStorage.setItem('groupName', groupName);
                  localStorage.setItem('groupType', groupType);
                  localStorage.setItem('groupDesc', groupDesc);
                  localStorage.setItem('groupIndustry', groupIndustry);

                  try {
                    await registerMutation({
                      variables: {
                        input: {
                          firstName,
                          lastName,
                          email,
                          password,
                          groupReferrerId: context.VRCId,
                          birthdate,
                          mailingList,
                        },
                      },
                    });

                    pushView(<VerifyEmail email={email} password={password} />);
                  } catch (err) {
                    setErrors(transformErrors(err));
                    setSubmitting(false);
                  }
                }}
              >
                {({ isSubmitting, setFieldValue, setFieldTouched, values, errors, touched }) => (
                  <Form>
                    <div className="Register-field">
                      <label htmlFor="firstName">First name</label>
                      <Field id="firstName" name="firstName" placeholder="Enter your first name" />
                    </div>
                    <ErrorMsg name="firstName" />

                    <div className="Register-field">
                      <label htmlFor="lastName">Last name</label>
                      <Field id="lastName" name="lastName" placeholder="Enter your last name" />
                    </div>
                    <ErrorMsg name="lastName" />

                    <div className="Register-field">
                      <label htmlFor="email">Email</label>
                      <Field id="email" type="email" name="email" placeholder="Enter your email" />
                    </div>
                    <ErrorMsg name="email" />

                    <div className="Register-field">
                      <label htmlFor="password">Password</label>
                      <PasswordMaskField id="password" name="password" placeholder="Enter your password" />
                    </div>
                    <ErrorMsg name="password" render={msg => <div className="ErrorMsg">{msg}</div>} />

                    <div className="Register-field">
                      <label htmlFor="birthdate">Date of birth</label>
                      <Field
                        id="birthdate"
                        type="birthdate"
                        name="birthdate"
                        render={({ field }) => (
                          <StyledDateInputFieldsWrapper>
                            <DateInputFields
                              fromYear={1900}
                              onChange={date => {
                                setFieldTouched(field.name);
                                setFieldValue(field.name, date);
                              }}
                            />
                          </StyledDateInputFieldsWrapper>
                        )}
                      />
                    </div>
                    <ErrorMsg name="birthdate" render={msg => <div className="ErrorMsg">{msg}</div>} />
                    <div className="Register-field Register-field-checkbox">
                      <Field
                        id="mailingList"
                        name="mailingList"
                        render={({ field }) => (
                          <React.Fragment>
                            <input
                              type="checkbox"
                              checked={field.value}
                              onChange={e => {
                                setFieldValue(field.name, e.target.checked);
                              }}
                            />
                            <span />
                          </React.Fragment>
                        )}
                      />
                      <label htmlFor="mailingList">I would like to receive communications from Be Collective</label>
                    </div>

                    {/* GROUP */}
                    <h3 className="Register-groupHeading">About your group</h3>

                    <div className="Register-field">
                      <label htmlFor="groupName">Name of group</label>
                      <Field id="groupName" name="groupName" placeholder="Enter your group name" />
                    </div>
                    <ErrorMsg name="groupName" />

                    <div className="Register-field is-half">
                      <div>
                        <div className="Register-field">
                          <label htmlFor="groupType">Group type</label>
                          <Field
                            id="groupType"
                            name="groupType"
                            render={({ field }) => (
                              <SelectWrap
                                onChange={data => {
                                  setFieldTouched(field.name);
                                  setFieldValue(field.name, data.target.value);
                                }}
                              >
                                <select>
                                  <option value="">Select Group Type</option>
                                  {GROUP_TYPES.map(({ name, value }) => {
                                    return (
                                      <option key={value} value={value}>
                                        {name}
                                      </option>
                                    );
                                  })}
                                </select>
                              </SelectWrap>
                            )}
                          />
                        </div>
                        <ErrorMsg name="groupType" />
                      </div>
                      {INDUSTRY_WITH_TYPES.includes(values.groupType) && (
                        <div>
                          <div className="Register-field">
                            <label htmlFor="groupIndustry">Industry / Sector *</label>
                            <Field id="groupIndustry" name="groupIndustry" component="select">
                              <option value="">Select Industry/Sector</option>
                              {groupIndustries.map(industry => {
                                return (
                                  <option key={industry} value={industry}>
                                    {industry}
                                  </option>
                                );
                              })}
                            </Field>
                          </div>
                          <ErrorMsg name="groupIndustry" />
                        </div>
                      )}
                    </div>

                    <div className="Register-field">
                      <label htmlFor="groupDesc">Description</label>
                      <Field
                        id="groupDesc"
                        component="textarea"
                        name="groupDesc"
                        placeholder="Tell everyone about your group"
                      />
                    </div>
                    <ErrorMsg name="groupDesc" />

                    <GraphQLErrorMessage errors={errors} />
                    <Button className="Register-submit" type="submit" disabled={isSubmitting}>
                      {isSubmitting ? 'Processing...' : registerSubmitButtonText || 'Register now'}
                    </Button>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        );
      }}
    </ThemeContext.Consumer>
  );
};

Register.propTypes = {
  registerMutation: P.func.isRequired,
  data: P.shape({
    store: P.shape({
      groupIndustries: P.arrayOf(P.string.isRequired).isRequired,
    }).isRequired,
  }).isRequired,
};

export default compose(
  graphql(REGISTER_USER_QUERY),
  graphql(REGISTER_USER_MUTATION, { name: 'registerMutation' }),
  ensureLoggedIn,
  makeStacker,
  displayLoading(),
)(Register);
